import React from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Stack,
} from "@mui/material";
import { Assessment, LocalShipping, TrendingUp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DashboardStats, Product } from "../../../../types";

interface AnalyticsTabProps {
  stats: DashboardStats;
  products: Product[];
}

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ stats, products }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {t("dashboard.analyticsReports")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 3,
        }}
      >
        <Box>
          <Card
            sx={{
              bgcolor: "background.paper",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Assessment sx={{ color: "#00d4ff", mr: 1 }} />
                <Typography variant="h6">
                  {t("dashboard.salesOverview")}
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                >
                  {t("dashboard.thisMonth")}
                </Typography>
                <Typography variant="h4" sx={{ color: "#00ff88" }}>
                  €{stats.total_revenue.toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2">
                  {t("common.orders")}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.total_orders}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">
                  {t("dashboard.averageOrderValue")}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  €
                  {stats.total_orders > 0
                    ? (
                        stats.total_revenue / stats.total_orders
                      ).toFixed(2)
                    : "0.00"}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card
            sx={{
              bgcolor: "background.paper",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <LocalShipping sx={{ color: "#ff0080", mr: 1 }} />
                <Typography variant="h6">
                  {t("dashboard.orderStatus")}
                </Typography>
              </Box>
              <Stack spacing={2}>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {t("orders.pending")}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stats.pending_orders}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      stats.total_orders > 0
                        ? (stats.pending_orders /
                            stats.total_orders) *
                          100
                        : 0
                    }
                    sx={{
                      bgcolor: "rgba(255,170,0,0.2)",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: "#ffaa00",
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {t("dashboard.completed")}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {stats.total_orders - stats.pending_orders}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      stats.total_orders > 0
                        ? ((stats.total_orders -
                            stats.pending_orders) /
                            stats.total_orders) *
                          100
                        : 0
                    }
                    sx={{
                      bgcolor: "rgba(0,255,136,0.2)",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: "#00ff88",
                      },
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
          <Card
            sx={{
              bgcolor: "background.paper",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <TrendingUp sx={{ color: "#00ff88", mr: 1 }} />
                <Typography variant="h6">
                  {t("dashboard.performanceMetrics")}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 3,
                }}
              >
                <Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      sx={{ color: "#00d4ff", fontWeight: 700 }}
                    >
                      {products.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {t("dashboard.activeProducts")}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      sx={{ color: "#ff0080", fontWeight: 700 }}
                    >
                      {stats.total_orders}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {t("dashboard.totalOrders")}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      sx={{ color: "#ffaa00", fontWeight: 700 }}
                    >
                      {
                        products.filter(
                          (p) => p.status === "available"
                        ).length
                      }
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {t("dashboard.availableProducts")}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h3"
                      sx={{ color: "#00ff88", fontWeight: 700 }}
                    >
                      €{stats.commission_earned.toFixed(0)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {t("dashboard.commissionEarned")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ gridColumn: { xs: "1", md: "span 2" } }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate("/admin/reports")}
            sx={{
              py: 2,
              background:
                "linear-gradient(135deg, #00ff88 0%, #00cc6f 100%)",
              boxShadow: "0 2px 8px rgba(0,255,136,0.2)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 3px 12px rgba(0,255,136,0.25)",
              },
            }}
          >
            {t("dashboard.viewDetailedReports")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AnalyticsTab;